import { useCallback } from "react";
import axiosInstance from "../helpers/axiosHelper";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import {
  getServiceWorkerUrl,
  isPushSupported,
  shouldReRegisterServiceWorker,
  urlBase64ToUint8Array,
} from "../utilities/pushUtility";
import { PushSubscribeResult } from "../../enums";

/** The worker's script URL carries the API address, so it can confirm deliveries back to it. */
const getExpectedServiceWorkerUrl = (): string =>
  getServiceWorkerUrl(axiosInstance.defaults.baseURL ?? "");

/**
 * Registers the push service worker. Safe to call more than once (the browser keeps one
 * registration per scope). Only called when the user turns notifications on, so users
 * who never do are not affected by a service worker at all.
 */
const registerPushServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register(getExpectedServiceWorkerUrl());
  } catch {
    return null;
  }
};

/** Existing registration without registering one, so status checks never install a worker. */
const getExistingRegistration = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }
  try {
    return (await navigator.serviceWorker.getRegistration()) ?? null;
  } catch {
    return null;
  }
};

const readPublicKey = (data: unknown): string | null => {
  // The API returns Ok(JsonSerializer.Serialize(new { publicKey })), which axios may surface
  // either as the parsed object or as a JSON string to parse again.
  try {
    const parsed = typeof data === "string" ? JSON.parse(data) : data;
    const publicKey = (parsed as { publicKey?: string })?.publicKey;
    return publicKey && publicKey.length > 0 ? publicKey : null;
  } catch {
    return null;
  }
};

export const usePushNotifications = () => {
  /**
   * Asks for notification permission, creates a push subscription and saves it on the
   * server. Must be called from a click: browsers (Safari above all) only show the
   * permission prompt in response to a user gesture, so permission is requested first.
   */
  const subscribe = useCallback(async (): Promise<PushSubscribeResult> => {
    if (!isPushSupported()) {
      return PushSubscribeResult.Unsupported;
    }

    let permission: NotificationPermission;
    try {
      permission = await Notification.requestPermission();
    } catch {
      return PushSubscribeResult.Failed;
    }

    if (permission === "denied") {
      return PushSubscribeResult.Denied;
    }
    if (permission !== "granted") {
      return PushSubscribeResult.Dismissed;
    }

    try {
      const registered = await registerPushServiceWorker();
      if (!registered) {
        return PushSubscribeResult.Failed;
      }
      const registration = await navigator.serviceWorker.ready;

      const response = await axiosInstance.get(endpointsMetadata.pushNotifications.publicKey);
      const publicKey = readPublicKey(response.data);
      if (!publicKey) {
        return PushSubscribeResult.Failed;
      }

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          // A fresh array, so its whole buffer is exactly the key bytes.
          applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
        });
      }

      await axiosInstance.post(endpointsMetadata.pushNotifications.subscribe, subscription.toJSON());
      return PushSubscribeResult.Subscribed;
    } catch {
      return PushSubscribeResult.Failed;
    }
  }, []);

  /** Removes this device's subscription from the browser and from the server. */
  const unsubscribe = useCallback(async (): Promise<void> => {
    const registration = await getExistingRegistration();
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      return;
    }

    const { endpoint } = subscription;
    await subscription.unsubscribe().catch(() => undefined);
    await axiosInstance.post(endpointsMetadata.pushNotifications.unsubscribe, { endpoint });
  }, []);

  /** True when this browser currently holds a push subscription. */
  const hasActiveSubscription = useCallback(async (): Promise<boolean> => {
    try {
      const registration = await getExistingRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      return !!subscription;
    } catch {
      return false;
    }
  }, []);

  /** This browser's push endpoint (the device's address for the server), or null. */
  const getActiveEndpoint = useCallback(async (): Promise<string | null> => {
    try {
      const registration = await getExistingRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      return subscription?.endpoint ?? null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Replaces a worker installed from an older script URL (before deliveries were confirmed,
   * or with another API address). Same scope, so the registration and its push subscription
   * are kept. Does nothing for someone without a worker: it never installs a new one.
   */
  const refreshServiceWorker = useCallback(async (): Promise<void> => {
    const registration = await getExistingRegistration();
    if (!registration) {
      return;
    }

    const expectedUrl = getExpectedServiceWorkerUrl();
    if (!shouldReRegisterServiceWorker(registration.active?.scriptURL, expectedUrl)) {
      return;
    }

    try {
      await navigator.serviceWorker.register(expectedUrl, { scope: registration.scope });
    } catch {
      // The old worker keeps showing notifications; we try again on the next visit.
    }
  }, []);

  return { subscribe, unsubscribe, hasActiveSubscription, getActiveEndpoint, refreshServiceWorker };
};
